import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactGridLayout, { useContainerWidth, verticalCompactor } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Box, GlobalStyles, Typography, alpha, useTheme } from '@mui/material';
import { GRID_COLUMNS, WIDGET_SIZES, columnsForWidth, defaultRowHeight, effectiveSize } from './sizes.js';
import {
  commitGridLayout,
  layoutsEqual,
  moveItemInOrder,
  nudgeItem,
  reflowToColumns,
  sizeOf,
  stepItemSize,
  toGridLayout,
} from './layoutModel.js';
import { isWidgetAllowed } from './widgetRegistry.js';
import { DRAG_CANCEL_SELECTOR, WidgetShell } from './WidgetShell.jsx';

/**
 * The dashboard grid (Beta-1 plan P3-15) — a thin wrapper over react-grid-layout that speaks
 * the canonical layout model (layoutModel.js) and enforces the size steps (sizes.js).
 *
 *   - Columns follow the measured container width: 4 desktop / 2 tablet / 1 phone
 *     (`cols` overrides, `breakpoints` tunes). Narrow grids render a projection of the stored
 *     4-column layout (reflowToColumns); edits made there are mapped back (commitGridLayout).
 *   - Widgets snap to the grid; a resize gesture is bounded by the widget's allowed sizes and
 *     snaps to the nearest step when it stops. Nothing is persisted here — the parent gets the
 *     new canonical layout through `onLayoutChange` and decides when to save (P3-19: on Done).
 *   - Widgets above the viewer's access level never render, even if a copied layout has them
 *     (P3-21); the layout keeps them so nothing is lost.
 *
 * Props
 *   layout           canonical layout { version, items }
 *   registry         WidgetRegistry
 *   editing          edit mode (drag/resize/remove enabled)
 *   role, dashboard  availability context (stored role key; DASHBOARD_KEYS)
 *   widgetContext    extra availability context (feature flags, …)
 *   cols             force a column count (playground / tests); default: measured
 *   rowHeight        px; default from the column width (sizes.defaultRowHeight)
 *   gutter           px between cells (default 16)
 *   onLayoutChange(layout)   canonical layout after a committed gesture (edit mode only)
 *   onRemove(i, title), onOpenSettings(i)  forwarded to the shells
 *   onRequestEdit()  long-press on a card (touch) asks the parent to enter edit mode (P3-16)
 *   highlightId      item id to scroll into view and highlight for ~2 s (the gallery's Add, P3-17);
 *                    `onHighlightEnd()` fires when the highlight is over
 *   renderEmpty()    node for an empty layout
 *
 * Edit mode (P3-16): cards wiggle (off under prefers-reduced-motion), every card is focusable —
 * arrow keys move it (on 2/1 columns: earlier/later in reading order), + / − step through its
 * allowed sizes, Delete/Backspace removes it; the grid itself never enters or leaves edit mode.
 */
export const LONG_PRESS_MS = 500;
export const HIGHLIGHT_MS = 2000;
export const EDIT_KEYBOARD_HELP =
  'Keyboard: Tab to a widget, arrow keys move it, + / − resize it, Delete removes it, Esc cancels.';

export function WidgetGrid({
  layout,
  registry,
  editing = false,
  role = null,
  dashboard = null,
  widgetContext = null,
  cols: colsProp,
  rowHeight: rowHeightProp,
  gutter = 16,
  breakpoints,
  onLayoutChange,
  onRemove,
  onOpenSettings,
  onRequestEdit,
  highlightId = null,
  onHighlightEnd,
  renderEmpty,
  sx,
}) {
  const theme = useTheme();
  const { width, containerRef, mounted } = useContainerWidth();
  const cols = colsProp || columnsForWidth(width, breakpoints);
  const columnWidth = width > 0 ? (width - gutter * (cols - 1)) / cols : 0;
  const rowHeight = rowHeightProp || defaultRowHeight(columnWidth);

  const items = layout?.items || [];

  const visibleItems = useMemo(
    () =>
      items.filter((it) => {
        const widget = registry.get(it.widgetId);
        return widget && isWidgetAllowed(widget, { role, dashboard, ...(widgetContext || {}) });
      }),
    [items, registry, role, dashboard, widgetContext]
  );
  const displayItems = useMemo(() => reflowToColumns(visibleItems, cols), [visibleItems, cols]);
  const gridLayout = useMemo(() => toGridLayout(displayItems, registry, cols), [displayItems, registry, cols]);
  const canonicalById = useMemo(() => new Map(items.map((it) => [it.i, it])), [items]);

  const handleLayoutChange = useCallback(
    (reported) => {
      // Only gestures count. RGL also reports when the layout prop changes (mount, breakpoint
      // change, a load from Firestore) — in view mode those echoes must not become edits.
      if (!editing || !onLayoutChange) return;
      const next = commitGridLayout(
        layout,
        reported.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })),
        cols,
        registry
      );
      if (!layoutsEqual(next, layout)) onLayoutChange(next);
    },
    [editing, onLayoutChange, layout, cols, registry]
  );

  const handleKeyDown = useCallback(
    (event, item, widget) => {
      if (!editing) return;
      const key = event.key;
      let next = null;
      if (key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown') {
        if (cols >= GRID_COLUMNS) {
          const dx = key === 'ArrowLeft' ? -1 : key === 'ArrowRight' ? 1 : 0;
          const dy = key === 'ArrowUp' ? -1 : key === 'ArrowDown' ? 1 : 0;
          next = nudgeItem(layout, item.i, dx, dy);
        } else {
          next = moveItemInOrder(layout, item.i, key === 'ArrowLeft' || key === 'ArrowUp' ? -1 : 1);
        }
      } else if (key === '+' || key === '=') {
        next = stepItemSize(layout, item.i, 1, registry);
      } else if (key === '-' || key === '_') {
        next = stepItemSize(layout, item.i, -1, registry);
      } else if (key === 'Delete' || key === 'Backspace') {
        if (onRemove) {
          event.preventDefault();
          onRemove(item.i, widget.title);
        }
        return;
      } else {
        return;
      }
      event.preventDefault();
      if (next && onLayoutChange && !layoutsEqual(next, layout)) onLayoutChange(next);
    },
    [editing, cols, layout, registry, onLayoutChange, onRemove]
  );

  // Gallery "Add": scroll the new card into view and flash it (P3-17).
  useEffect(() => {
    if (!highlightId || !containerRef.current) return undefined;
    const node = containerRef.current.querySelector(`.widget-grid-item[data-item-id="${CSS.escape(highlightId)}"]`);
    if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => onHighlightEnd && onHighlightEnd(), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlightId, onHighlightEnd, containerRef]);

  // Touch long-press on a card enters edit mode (tablets/phones, P3-16).
  const press = useRef(null);
  const clearPress = useCallback(() => {
    if (press.current) {
      clearTimeout(press.current.timer);
      press.current = null;
    }
  }, []);
  useEffect(() => clearPress, [clearPress]);
  const handlePointerDown = useCallback(
    (event) => {
      if (editing || !onRequestEdit || event.pointerType !== 'touch') return;
      if (!event.target.closest('.widget-grid-item')) return;
      clearPress();
      const start = { x: event.clientX, y: event.clientY };
      press.current = {
        start,
        timer: setTimeout(() => {
          press.current = null;
          onRequestEdit();
        }, LONG_PRESS_MS),
      };
    },
    [editing, onRequestEdit, clearPress]
  );
  const handleContextMenu = useCallback((event) => {
    if (press.current) event.preventDefault(); // iOS/Android callout during a long-press
  }, []);
  const handlePointerMove = useCallback(
    (event) => {
      if (!press.current) return;
      const { start } = press.current;
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8) clearPress();
    },
    [clearPress]
  );

  const empty = displayItems.length === 0;

  return (
    <Box
      ref={containerRef}
      className="widget-grid-container"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={clearPress}
      onPointerCancel={clearPress}
      onContextMenu={handleContextMenu}
      sx={{ width: '100%', minHeight: empty ? 120 : rowHeight, ...sx }}
    >
      <GlobalStyles
        styles={{
          '.widget-grid .react-grid-item': { borderRadius: theme.shape.borderRadius },
          '.widget-grid .react-grid-item.react-grid-placeholder': {
            background: alpha(theme.palette.primary.main, 0.16),
            border: `2px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
            borderRadius: theme.shape.borderRadius,
            opacity: 1,
          },
          '.widget-grid .react-grid-item.react-draggable-dragging .widget-shell': {
            boxShadow: theme.shadows[12],
            transform: 'scale(1.02)',
          },
          '.widget-grid .react-grid-item.resizing .widget-shell': { boxShadow: theme.shadows[8] },
          '.widget-grid .react-grid-item > .react-resizable-handle': {
            width: 24,
            height: 24,
            right: 4,
            bottom: 4,
            padding: 0,
            backgroundImage: 'none',
            cursor: 'nwse-resize',
            zIndex: 2,
          },
          '.widget-grid .react-grid-item > .react-resizable-handle::after': {
            content: '""',
            position: 'absolute',
            right: 6,
            bottom: 6,
            width: 10,
            height: 10,
            borderRight: `3px solid ${theme.palette.text.secondary}`,
            borderBottom: `3px solid ${theme.palette.text.secondary}`,
            borderBottomRightRadius: 4,
          },
          '.widget-grid-item, .widget-grid-item > .widget-shell': { height: '100%' },
          '.widget-grid-item--stacked, .widget-grid-item--stacked > .widget-shell': { height: 'auto' },
          '.widget-grid-item:focus': { outline: 'none' },
          '.widget-grid--editing .widget-grid-item:focus-visible > .widget-shell': {
            outline: `3px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
          '@keyframes widget-highlight': {
            '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.7)}` },
            '60%': { boxShadow: `0 0 0 10px ${alpha(theme.palette.primary.main, 0)}` },
            '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}` },
          },
          '.widget-grid-item.widget-grid-item--highlight > .widget-shell': {
            outline: `3px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
            animation: 'widget-highlight 1s ease-out 2',
          },
          '@keyframes widget-wiggle': {
            '0%': { transform: 'rotate(-0.6deg)' },
            '50%': { transform: 'rotate(0.6deg)' },
            '100%': { transform: 'rotate(-0.6deg)' },
          },
          // RGL clones the child, so `.react-grid-item` and `.widget-grid-item` are the SAME element.
          '.widget-grid--editing .widget-grid-item:not(.react-draggable-dragging):not(.resizing) > .widget-shell': {
            animation: 'widget-wiggle 0.32s ease-in-out infinite',
            transformOrigin: '50% 50%',
          },
          '.widget-grid--editing .widget-grid-item:nth-of-type(2n) > .widget-shell': { animationDelay: '-0.08s' },
          '.widget-grid--editing .widget-grid-item:nth-of-type(3n) > .widget-shell': { animationDelay: '-0.16s' },
          '.widget-grid--editing .widget-grid-item:focus-within > .widget-shell': { animation: 'none' },
          '@media (prefers-reduced-motion: reduce)': {
            '.widget-grid--editing .widget-grid-item > .widget-shell': { animation: 'none' },
          },
        }}
      />
      {mounted && empty && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          {renderEmpty ? (
            renderEmpty()
          ) : (
            <Typography variant="body2" color="text.secondary">
              No widgets on this dashboard yet.
            </Typography>
          )}
        </Box>
      )}
      {/* Phone and tablet (one or two columns, not editing): a plain stack with natural heights.
          At two columns every M/W/L/XL widget is already full width, so the fixed-row grid only
          clipped tall cards or left short ones half empty (UI pass 2026-09-21). Each item keeps one
          row as a floor and otherwise takes its natural height; editing keeps the grid. */}
      {mounted && !empty && cols <= 2 && !editing && (
        <Box className="widget-grid widget-grid--stacked" sx={{ display: 'flex', flexDirection: 'column', gap: `${gutter}px` }}>
          {displayItems.map((display) => {
            const item = canonicalById.get(display.i) || display;
            const widget = registry.get(item.widgetId);
            const canonicalSize = sizeOf(item) || widget.defaultSize;
            const size = effectiveSize(canonicalSize, cols);
            const Component = widget.component;
            return (
              <Box
                key={item.i}
                className="widget-grid-item widget-grid-item--stacked"
                data-item-id={item.i}
                data-widget-id={widget.id}
                data-size={canonicalSize}
                sx={{ minHeight: rowHeight, display: 'flex', flexDirection: 'column', '& > .widget-shell': { flex: 1 } }}
              >
                <WidgetShell widget={widget} item={item} size={size} editing={false} onOpenSettings={onOpenSettings}>
                  <Component item={item} size={size} canonicalSize={canonicalSize} props={item.props || {}} editing={false} cols={cols} />
                </WidgetShell>
              </Box>
            );
          })}
        </Box>
      )}
      {mounted && !empty && !(cols <= 2 && !editing) && (
        <ReactGridLayout
          className={`widget-grid${editing ? ' widget-grid--editing' : ''}`}
          layout={gridLayout}
          width={width}
          gridConfig={{ cols, rowHeight, margin: [gutter, gutter], containerPadding: [0, 0] }}
          dragConfig={{ enabled: editing, bounded: true, cancel: DRAG_CANCEL_SELECTOR }}
          resizeConfig={{ enabled: editing, handles: ['se'] }}
          compactor={verticalCompactor}
          onLayoutChange={handleLayoutChange}
        >
          {displayItems.map((display) => {
            const item = canonicalById.get(display.i) || display;
            const widget = registry.get(item.widgetId);
            const canonicalSize = sizeOf(item) || widget.defaultSize;
            const size = effectiveSize(canonicalSize, cols);
            const Component = widget.component;
            return (
              <div
                key={item.i}
                className={`widget-grid-item${highlightId === item.i ? ' widget-grid-item--highlight' : ''}`}
                data-item-id={item.i}
                data-widget-id={widget.id}
                data-size={canonicalSize}
                tabIndex={editing ? 0 : -1}
                role={editing ? 'group' : undefined}
                aria-label={editing ? `${widget.title}, ${WIDGET_SIZES[canonicalSize]?.label || canonicalSize}. ${EDIT_KEYBOARD_HELP}` : undefined}
                onKeyDown={editing ? (event) => handleKeyDown(event, item, widget) : undefined}
              >
                <WidgetShell
                  widget={widget}
                  item={item}
                  size={size}
                  editing={editing}
                  onRemove={editing && onRemove ? (i) => onRemove(i, widget.title) : undefined}
                  onOpenSettings={onOpenSettings}
                >
                  <Component item={item} size={size} canonicalSize={canonicalSize} props={item.props || {}} editing={editing} cols={cols} />
                </WidgetShell>
              </div>
            );
          })}
        </ReactGridLayout>
      )}
    </Box>
  );
}

export default WidgetGrid;
